/**
 * Flat target-account folders (from `listFoldersForTargetAccount`) plus any
 * newly-created folders we materialized inline via
 * `createFolderInTargetAccount`. We reconstruct the tree client-side.
 */

/** Folder as returned by the BFF `listFoldersForTargetAccount` procedure. */
export interface TargetFolder {
  id: string;
  name: string;
  accountId: number;
  ownerId: number | null;
  isShared: boolean;
  status: string;
  parentId: string | null;
  rootId: string | null;
}

/**
 * A newly-created folder the user materialized from the modal. Flagged so the
 * UI can show the trash icon only on entries we own in this session.
 */
export interface NewlyCreatedFolder {
  id: string;
  name: string;
  parentId: string | null;
  isNew: true;
}

/** Entry kind either came from the server list or was created inline here. */
export type FolderTreeNode = (TargetFolder | NewlyCreatedFolder) & {
  children: FolderTreeNode[];
  depth: number;
};

/**
 * Builds a depth-ordered (DFS) flat list of folder tree nodes from the
 * server-side folders + in-session newly-created folders. Orphan nodes
 * (parent not in the list) are dropped — should not happen in practice since
 * we control creation.
 */
export function buildFolderTree(
  serverFolders: TargetFolder[],
  newFolders: NewlyCreatedFolder[],
): FolderTreeNode[] {
  const byParent = new Map<string | null, FolderTreeNode[]>();

  const toNode = (
    f: TargetFolder | NewlyCreatedFolder,
    depth: number,
  ): FolderTreeNode =>
    ({
      ...f,
      depth,
      children: [],
    }) as FolderTreeNode;

  // Once a freshly-created folder shows up in the server response (after
  // the post-create list-query invalidation refetches), the same id can
  // exist in both `serverFolders` and `newFolders`. Skip the server copy
  // for ids the user just created in this session so:
  //   - the row doesn't render twice
  //   - the trash icon (driven by the `isNew: true` flag on `newFolders`)
  //     stays available until the modal closes
  // After modal close + reopen, `newFolders` is empty and the row renders
  // once from server data alone, with no trash icon (expected).
  const newFolderIds = new Set(newFolders.map((f) => f.id));

  for (const f of serverFolders) {
    if (newFolderIds.has(f.id)) continue;
    // Server folders have `parentId: string | null`; null means root folder.
    // eslint-disable-next-line unicorn/no-null
    const parentKey = f.parentId ?? null;
    if (!byParent.has(parentKey)) byParent.set(parentKey, []);
    byParent.get(parentKey)!.push(toNode(f, 0));
  }
  for (const f of newFolders) {
    const parentKey = f.parentId;
    if (!byParent.has(parentKey)) byParent.set(parentKey, []);
    byParent.get(parentKey)!.push(toNode(f, 0));
  }

  // DFS starting from roots to assign depth + link children. Server folders
  // sort alphabetically as before; newly-created folders are pinned to the
  // *end* of their parent's children in insertion order so they don't jump
  // around alphabetically when committed (matching where the
  // create-input also appears at the bottom of the parent's children).
  const flat: FolderTreeNode[] = [];
  const visit = (parentId: string | null, depth: number) => {
    const kids = byParent.get(parentId) ?? [];
    kids.sort((a, b) => {
      const aIsNew = newFolderIds.has(a.id);
      const bIsNew = newFolderIds.has(b.id);
      if (aIsNew !== bIsNew) return aIsNew ? 1 : -1;
      // Preserve insertion order among newly-created siblings.
      if (aIsNew && bIsNew) return 0;
      return a.name.localeCompare(b.name);
    });
    for (const kid of kids) {
      kid.depth = depth;
      flat.push(kid);
      visit(kid.id, depth + 1);
    }
  };
  // eslint-disable-next-line unicorn/no-null
  visit(null, 0);
  return flat;
}

/**
 * Given a `NewlyCreatedFolder`, return all ids of newly-created descendants
 * in deepest-first order — the cascade-delete list passed to the BFF.
 */
export function collectNewlyCreatedDescendants(
  rootId: string,
  newFolders: NewlyCreatedFolder[],
): string[] {
  const byParent = new Map<string, NewlyCreatedFolder[]>();
  for (const f of newFolders) {
    if (f.parentId === null) continue;
    if (!byParent.has(f.parentId)) byParent.set(f.parentId, []);
    byParent.get(f.parentId)!.push(f);
  }
  const result: string[] = [];
  const visit = (id: string) => {
    for (const child of byParent.get(id) ?? []) {
      visit(child.id);
      result.push(child.id);
    }
  };
  visit(rootId);
  // Deepest first (visit is post-order).
  return result;
}

/** Type guard: is this entry a newly-created-in-session folder? */
export function isNewlyCreated(
  node: FolderTreeNode,
): node is FolderTreeNode & NewlyCreatedFolder {
  return (node as NewlyCreatedFolder).isNew === true;
}

/**
 * Whether the current user owns this folder. Folders created inline in this
 * session are always treated as owned (the user just made them); server
 * folders match on `ownerId`.
 */
export function isFolderOwnedByUser(
  node: FolderTreeNode,
  userId: number,
): boolean {
  if (isNewlyCreated(node)) return true;
  return node.ownerId === userId;
}

interface FilterFolderTreeOptions {
  /** Case-insensitive substring match against folder name. */
  searchQuery?: string;
  /**
   * When a numeric id is provided, keep only folders owned by that user
   * (plus the ancestors needed to reach them). `undefined`/`null` disables
   * the ownership filter.
   */
  ownedByMeUserId?: number | null;
}

/**
 * Returns the set of folder ids that should be visible for the given filters.
 * A folder is kept when it matches every active filter; its full ancestor
 * chain is also kept so the match stays reachable inside the tree. When no
 * filter is active, every id is returned (caller falls back to its own
 * expand/collapse visibility instead).
 */
export function filterFolderTree(
  flatTree: FolderTreeNode[],
  options: FilterFolderTreeOptions,
): Set<string> {
  const normalizedQuery = options.searchQuery?.trim().toLowerCase() ?? "";
  const isSearchActive = normalizedQuery.length > 0;
  const isOwnerActive = typeof options.ownedByMeUserId === "number";

  if (!isSearchActive && !isOwnerActive) {
    return new Set(flatTree.map((node) => node.id));
  }

  const parentIdByChildId = new Map<string, string | null>();
  for (const node of flatTree) {
    // eslint-disable-next-line unicorn/no-null
    parentIdByChildId.set(node.id, node.parentId ?? null);
  }

  const visible = new Set<string>();
  for (const node of flatTree) {
    const matchesSearch =
      !isSearchActive || node.name.toLowerCase().includes(normalizedQuery);
    const matchesOwner =
      !isOwnerActive ||
      isFolderOwnedByUser(node, options.ownedByMeUserId as number);
    if (!matchesSearch || !matchesOwner) continue;

    // Add the match plus every ancestor so it remains reachable in the tree.
    visible.add(node.id);
    let cursor = parentIdByChildId.get(node.id) ?? undefined;
    while (cursor) {
      if (visible.has(cursor)) break;
      visible.add(cursor);
      cursor = parentIdByChildId.get(cursor) ?? undefined;
    }
  }
  return visible;
}
